import 'react-dates/initialize'
import type { ReactElement } from 'react'
import { Children, isValidElement, useState, useEffect } from 'react'
import { Router } from 'react-router'
import { ApolloProvider } from '@apollo/client'
import { QueryClientProvider } from '@tanstack/react-query'
import flow from 'lodash-es/flow'
import map from 'lodash-es/map'
import compact from 'lodash-es/compact'
import flatten from 'lodash-es/flatten'
import Favicon from 'react-favicon'
import {
  I18nProvider,
  useI18nContext,
  type IPP_SUPPORTED_LOCALE,
  IPP_DEFAULT_LOCALE,
  getStoredLocaleWithFallback,
} from '@retailer-platform/shared-components/src/utils/intl'
import EN_US_TRANSLATIONS from '../../intl/translations/en-US.json'
import ES_ES_TRANSLATIONS from '../../intl/translations/es-ES.json'
import FR_FR_TRANSLATIONS from '../../intl/translations/fr-FR.json'
import {
  AppProvider,
  importAppUIProviders,
} from '../../gin-and-tonic/containers/app-provider/AppProvider'
import { AppProviderPreloader } from '../../gin-and-tonic/containers/app-provider/components/AppProviderPreloader'
import { fetchDashboardClient } from '../../legacy/apollo/dashboardClient'
import { queryClient } from '../query-client/queryClient'
import createHistory from '../../legacy/common/history'
import { ROUTES_BY_NAME } from '../routing/routes'
import RPP_FAVICON from '../../assets/favicon.png'
import { setTotemRouteOwnerMapping } from '../events/analytics'
import type { Instacart_Infra_Totem_V1_System as TotemSystem } from '../../__codegen__/api'
import { createMessagesWithFallback } from '../../intl/intl.utils'
import { loadDayjsLocale } from '../query-params/dayjsDate.utils'
import { loadMomentLocale } from '../query-params/momentDate.utils'
import { DASHBOARD_ROUTE_TOOLS } from '../routing/dashRouteTools'
import { type NavEntryPointV3 } from '../../gin-and-tonic/containers/nav-v3/navEntryPointsV3'
import { type AdminNavEntryPointV3 } from '../../gin-and-tonic/containers/nav-v3/adminNavEntryPointsV3'
import { CoreContext } from './RPPCoreContext'
import { MockGate } from './MockGate'
import {
  type RPPDomain,
  type RPPCoreContext,
  type RPPRouter,
  type RPPDomainNavigationEntriesByAttachType,
  type RPPDomainAdminNavigationEntriesByAttachType,
  type RPPNavigationEntry,
} from './RPPCore.types'

/**
 * This class represents our base application, where multiple domains
 * are registered, and a context generated based on all defined domains
 * which is then passed throughout the application.
 */
export class RPPCore {
  domains: RPPDomain<any, any, any>[]
  additionalTranslations: Record<IPP_SUPPORTED_LOCALE, Record<string, string>>

  constructor({ domains = [] }: { domains?: RPPDomain<any, any, any>[] }) {
    this.domains = domains
    this.additionalTranslations = {
      'en-US': {},
      'es-ES': {},
      'es-US': {},
      'fr-FR': {},
      'fr-CA': {},
    }
  }

  attachDomain(domain: RPPDomain<any, any, any>) {
    this.domains.push(domain)
  }

  getLocaleMessages<T extends Record<string, string>>(
    locale: IPP_SUPPORTED_LOCALE,
    messages: {
      'en-US': T
      'es-ES'?: Partial<T>
      'es-US'?: Partial<T>
      'fr-FR'?: Partial<T>
      'fr-CA'?: Partial<T>
    }
  ): T {
    const fallback = messages[IPP_DEFAULT_LOCALE] as T
    if (locale === IPP_DEFAULT_LOCALE) {
      return fallback
    }
    const partialMessages = messages[locale] || {}
    return createMessagesWithFallback(partialMessages as T, fallback)
  }

  getDashboardTranslations(locale: IPP_SUPPORTED_LOCALE = IPP_DEFAULT_LOCALE) {
    return this.getLocaleMessages(locale, {
      'en-US': EN_US_TRANSLATIONS,
      'es-ES': ES_ES_TRANSLATIONS,
      'fr-FR': FR_FR_TRANSLATIONS,
    })
  }

  getAdditionalTranslations(locale: IPP_SUPPORTED_LOCALE = IPP_DEFAULT_LOCALE) {
    return this.getLocaleMessages(locale, this.additionalTranslations)
  }

  getDomainTranslations(locale: IPP_SUPPORTED_LOCALE = IPP_DEFAULT_LOCALE) {
    return this.domains.map(domain =>
      this.getLocaleMessages(locale, {
        'en-US': domain.translations?.EN_US || {},
        'es-ES': domain.translations?.ES_ES || {},
        'fr-FR': domain.translations?.FR_FR || {},
      })
    )
  }

  /**
   * Load lazy translations for non-English locales.
   * This method loads translations asynchronously and populates domain.translations
   * so that getDomainTranslations() can access them synchronously.
   *
   * @param locale - The locale to load translations for
   * @returns Promise that resolves when all lazy translations are loaded
   */
  async loadLazyTranslations(locale: IPP_SUPPORTED_LOCALE): Promise<void> {
    // No lazy loading needed for English (default locale)
    if (locale === IPP_DEFAULT_LOCALE) return

    // Map locale to translation key
    const localeKey: 'ES_ES' | 'FR_FR' | null =
      locale === 'es-ES' || locale === 'es-US'
        ? 'ES_ES'
        : locale === 'fr-FR' || locale === 'fr-CA'
          ? 'FR_FR'
          : null

    if (!localeKey) return

    // Load translations for all domains in parallel
    await Promise.all(
      this.domains.map(async domain => {
        const loader = domain.lazyTranslations?.[localeKey]
        if (loader && !domain.translations[localeKey]) {
          try {
            const module = await loader()
            domain.translations[localeKey] = module.default
          } catch (error) {
            // Translation file may not exist for this domain/locale - that's OK
            console.debug(`No ${localeKey} translations for domain ${domain.id}`)
          }
        }
      })
    )
  }

  // Cache for merged translations by locale
  private translationsCache: Partial<Record<IPP_SUPPORTED_LOCALE, Record<string, string>>> = {}

  getTranslations(locale: IPP_SUPPORTED_LOCALE = IPP_DEFAULT_LOCALE) {
    // Return cached translations if available
    if (this.translationsCache[locale]) {
      return this.translationsCache[locale]
    }

    const domainTranslations = this.getDomainTranslations(locale)
    const dashboardTranslations = this.getDashboardTranslations(locale)
    const additionalTranslations = this.getAdditionalTranslations(locale)

    const merged = Object.assign(
      {},
      dashboardTranslations,
      additionalTranslations,
      ...domainTranslations
    )

    // Cache the result
    this.translationsCache[locale] = merged
    return merged
  }

  addTranslations(
    translationObject: Partial<Record<IPP_SUPPORTED_LOCALE, Record<string, string>>>
  ) {
    this.additionalTranslations['en-US'] = {
      ...this.additionalTranslations['en-US'],
      ...translationObject['en-US'],
    }

    if (translationObject['es-ES']) {
      this.additionalTranslations['es-ES'] = {
        ...this.additionalTranslations['es-ES'],
        ...translationObject['es-ES'],
      }
    }

    if (translationObject['fr-FR']) {
      this.additionalTranslations['fr-FR'] = {
        ...this.additionalTranslations['fr-FR'],
        ...translationObject['fr-FR'],
      }
    }
  }

  /**
   * Groups the retailer-scoped navigation entries from the domains by attach type
   */
  getDomainNavigationEntriesByAttachType(): RPPDomainNavigationEntriesByAttachType {
    return this.domains.reduce((allNavEntries, domain) => {
      if (!domain.navigationEntries) return allNavEntries

      domain.navigationEntries.forEach(({ subNavItems, attachToV2 }) => {
        if (!attachToV2) return

        // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
        const newEntries: RPPNavigationEntry[] = subNavItems
          .map(subNavItem => {
            if (subNavItem.type === 'header') return null
            return {
              route: subNavItem.route as string,
              llmDescription: subNavItem.llmDescription,
              subRoutes: subNavItem.subRoutes as string[],
              href: subNavItem.href as string,
              labelId: subNavItem.labelId,
              accessControl: subNavItem.accessControl,
              id: subNavItem.id,
              position: subNavItem.position,
              positionNavV2: subNavItem.positionNavV2,
              isNew: subNavItem.isNew,
              navBadge: subNavItem.navBadge,
              environment: subNavItem.environment,
              NavItemWrapper: subNavItem.NavItemWrapper,
              inToolPermissions: subNavItem.inToolPermissions,
              domainId: domain.id,
            }
          })
          .filter(Boolean)

        allNavEntries[attachToV2] = [...(allNavEntries[attachToV2] || []), ...newEntries]
      })

      return allNavEntries
    }, {} as RPPDomainNavigationEntriesByAttachType)
  }

  /**
   * Groups the retailer-scoped navigation entries from the domains by their v3 attach point.
   * Reads the same `navigationEntries` array as v2; entries that omit `attachToV3` are skipped.
   */
  getDomainNavigationEntriesByAttachTypeV3(): Partial<
    Record<NavEntryPointV3, RPPNavigationEntry[]>
  > {
    return this.domains.reduce(
      (allNavEntries, domain) => {
        if (!domain.navigationEntries) return allNavEntries

        domain.navigationEntries.forEach(({ subNavItems, attachToV3 }) => {
          if (!attachToV3) return

          // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
          const newEntries: RPPNavigationEntry[] = subNavItems
            .map(subNavItem => {
              if (subNavItem.type === 'header') return null
              return {
                route: subNavItem.route as string,
                llmDescription: subNavItem.llmDescription,
                subRoutes: subNavItem.subRoutes as string[],
                href: subNavItem.href as string,
                labelId: subNavItem.labelId,
                labelIdV3: subNavItem.labelIdV3,
                accessControl: subNavItem.accessControl,
                id: subNavItem.id,
                position: subNavItem.position,
                positionNavV2: subNavItem.positionNavV2,
                positionNavV3: subNavItem.positionNavV3,
                isNew: subNavItem.isNew,
                navBadge: subNavItem.navBadge,
                environment: subNavItem.environment,
                NavItemWrapper: subNavItem.NavItemWrapper,
                inToolPermissions: subNavItem.inToolPermissions,
                dividerBefore: subNavItem.dividerBefore,
                domainId: domain.id,
              }
            })
            .filter(Boolean)

          allNavEntries[attachToV3] = [...(allNavEntries[attachToV3] || []), ...newEntries]
        })

        return allNavEntries
      },
      {} as Partial<Record<NavEntryPointV3, RPPNavigationEntry[]>>
    )
  }

  /**
   * Groups the admin navigation entries from the domains by attach type
   */
  getDomainAdminNavigationEntriesByAttachType(): RPPDomainAdminNavigationEntriesByAttachType {
    return this.domains.reduce((allNavEntries, domain) => {
      if (!domain.adminNavigationEntries) return allNavEntries

      domain.adminNavigationEntries.forEach(({ subNavItems, attachToV2 }) => {
        if (!attachToV2) return

        const newEntries: RPPNavigationEntry[] = subNavItems.map(subNavItem => ({
          route: subNavItem.route as string,
          subRoutes: subNavItem.subRoutes as string[],
          labelId: subNavItem.labelId,
          accessControl: subNavItem.accessControl,
          positionNavV2: subNavItem.positionNavV2,
          domainId: domain.id,
        }))

        allNavEntries[attachToV2] = [...(allNavEntries[attachToV2] || []), ...newEntries]
      })

      return allNavEntries
    }, {} as RPPDomainAdminNavigationEntriesByAttachType)
  }

  /**
   * Groups the admin navigation entries from the domains by their v3 attach point.
   * Reads the same `adminNavigationEntries` array as v2; entries that omit
   * `attachToV3` are skipped.
   */
  getDomainAdminNavigationEntriesByAttachTypeV3(): Partial<
    Record<AdminNavEntryPointV3, RPPNavigationEntry[]>
  > {
    return this.domains.reduce(
      (allNavEntries, domain) => {
        if (!domain.adminNavigationEntries) return allNavEntries

        domain.adminNavigationEntries.forEach(({ subNavItems, attachToV3 }) => {
          if (!attachToV3) return

          const newEntries: RPPNavigationEntry[] = subNavItems.map(subNavItem => ({
            route: subNavItem.route as string,
            subRoutes: subNavItem.subRoutes as string[],
            labelId: subNavItem.labelId,
            labelIdV3: subNavItem.labelIdV3,
            accessControl: subNavItem.accessControl,
            positionNavV2: subNavItem.positionNavV2,
            positionNavV3: subNavItem.positionNavV3,
            inToolPermissions: subNavItem.inToolPermissions,
            dividerBefore: subNavItem.dividerBefore,
            domainId: domain.id,
          }))

          allNavEntries[attachToV3] = [...(allNavEntries[attachToV3] || []), ...newEntries]
        })

        return allNavEntries
      },
      {} as Partial<Record<AdminNavEntryPointV3, RPPNavigationEntry[]>>
    )
  }

  /**
   * Merges all routes
   */
  getRoutes(): Record<string, string> {
    return this.domains.reduce((acc, domain) => ({ ...acc, ...domain.routes }), {
      ...ROUTES_BY_NAME,
    })
  }

  /**
   * Recursively extract route-to-tool mappings from router JSX elements
   * This walks through the router tree and collects any DomainRoute elements that have a `tool` prop
   */
  private extractRouteToolsFromElement(
    element: ReactElement,
    routeToToolMap: Map<string, string>
  ): void {
    if (!isValidElement(element)) return

    // Check if this element has a route and tool prop
    const props = element.props as any
    if (props?.route && props?.tool) {
      routeToToolMap.set(props.route, props.tool)
    }

    // Recursively check children
    if (props?.children) {
      Children.forEach(props.children, child => {
        if (isValidElement(child)) {
          this.extractRouteToolsFromElement(child, routeToToolMap)
        }
      })
    }
  }

  /**
   * Extract route-to-tool mappings from all routers across all domains and dashboard routes
   */
  private extractRouteToolsFromRouters(): Map<string, string> {
    const routeToToolMap = new Map<string, string>()

    // Extract from domain routers
    this.domains.forEach(domain => {
      // Extract from all router types
      const allRouters = [
        ...(domain.routers.admin || []),
        ...(domain.routers.scoped || []),
        ...(domain.routers.partnerScoped || []),
        ...(domain.routers.public || []),
      ]

      allRouters.forEach(router => {
        if (isValidElement(router)) {
          this.extractRouteToolsFromElement(router, routeToToolMap)
        }
      })
    })

    // Extract from dashboard routes (core routes defined in packages/dashboard/src/routes)
    this.extractDashboardRouteTools(routeToToolMap)
    return routeToToolMap
  }

  /**
   * Extract tool props from dashboard routes using the static DASHBOARD_ROUTE_TOOLS registry
   * This captures routes like store-groups, orders, settings that are defined in the core dashboard
   *
   * NOTE: This is temporary until we move the tools out of dashboard into their respective domains.
   */
  private extractDashboardRouteTools(routeToToolMap: Map<string, string>): void {
    // Add dashboard route tools from the static registry
    Object.entries(DASHBOARD_ROUTE_TOOLS).forEach(([routeName, tool]) => {
      routeToToolMap.set(routeName, tool)

      // Validate that the route actually exists in ROUTES_BY_NAME
      if (!(ROUTES_BY_NAME as Record<string, unknown>)[routeName]) {
        console.warn(
          `[RPPCore] Dashboard route '${routeName}' is defined in DASHBOARD_ROUTE_TOOLS but not found in ROUTES_BY_NAME. ` +
            `This entry will be ignored.`
        )
      }
    })
  }

  /**
   * Maps routes to totem info for all domains and dashboard routes
   *
   * Priority order for DOMAIN routes:
   * 1. Route-level tool (defined on DomainRoute component `tool` prop) - extracted from JSX
   * 2. Fall back to domain default tool
   *
   * For DASHBOARD routes:
   * - Uses DASHBOARD_ROUTE_TOOLS static registry
   * - This is temporary until we move the tools out of dashboard into their respective domains.
   */
  getTotemInfoByRoute(): Record<
    string,
    { routePattern: string; hasDevTooling?: boolean; totemSystem?: TotemSystem }
  > {
    const routeOwnerMapping: Record<string, { routePattern: string; hasDevTooling?: boolean }> = {}

    // Extract route-level tools from router JSX elements (both domain and dashboard routes)
    const routeToolMap = this.extractRouteToolsFromRouters()

    // Process domain routes
    this.domains.forEach(domain => {
      Object.entries(domain.routes).forEach(([routeName, routePattern]) => {
        // Check route-level tool (from DomainRoute component `tool` prop)
        const routeLevelTool = routeToolMap.get(routeName)

        if (routeLevelTool) {
          routeOwnerMapping[routePattern as string] = {
            routePattern: routeLevelTool,
            hasDevTooling: domain.totem?.hasDevTooling,
          }
          return
        }

        // Fall back to domain default tool
        if (domain.totem?.tool) {
          routeOwnerMapping[routePattern as string] = {
            routePattern: domain.totem.tool,
            hasDevTooling: domain.totem.hasDevTooling,
          }
        }
      })
    })

    // Process dashboard routes (core routes from ROUTES_BY_NAME)
    Object.entries(ROUTES_BY_NAME).forEach(([routeName, routePattern]) => {
      // Only add dashboard routes that have an explicit tool prop
      const routeLevelTool = routeToolMap.get(routeName)
      if (routeLevelTool) {
        routeOwnerMapping[routePattern as string] = {
          routePattern: routeLevelTool,
          hasDevTooling: undefined, // Dashboard routes don't have domain-level hasDevTooling
        }
      }
    })

    setTotemRouteOwnerMapping(routeOwnerMapping)
    return routeOwnerMapping
  }

  /**
   * Extracts the admin routers of each domain and collects them into a flattened array
   */
  getAdminRouters(): RPPRouter[] {
    return flow([a => map(a, 'routers.admin'), flatten, compact])(this.domains)
  }

  /**
   * Extracts the dev portal routers of each domain and collects them into a flattened array
   */
  getDevPortalRouters(): RPPRouter[] {
    return flow([a => map(a, 'routers.devPortal'), flatten, compact])(this.domains)
  }

  /**
   * Extracts the scoped routers of each domain and collects them into a flattened array
   */
  getScopedRouters(): RPPRouter[] {
    return flow([a => map(a, 'routers.scoped'), flatten, compact])(this.domains)
  }

  /**
   * Extracts the scoped routers of each domain and collects them into a flattened array
   */
  getPartnerScopedRouters(): RPPRouter[] {
    return flow([a => map(a, 'routers.partnerScoped'), flatten, compact])(this.domains)
  }

  /**
   * Extracts the public routers of each domain and collects them into a flattened array
   */
  getPublicRouters(): RPPRouter[] {
    return flow([a => map(a, 'routers.public'), flatten, compact])(this.domains)
  }

  prepareContext(): RPPCoreContext {
    return {
      routesByName: this.getRoutes(),
      adminRouters: this.getAdminRouters(),
      scopedRouters: this.getScopedRouters(),
      partnerScopedRouters: this.getPartnerScopedRouters(),
      publicRouters: this.getPublicRouters(),
      domainNavigationEntriesV2: this.getDomainNavigationEntriesByAttachType(),
      domainAdminNavigationEntriesV2: this.getDomainAdminNavigationEntriesByAttachType(),
      domainNavigationEntriesV3: this.getDomainNavigationEntriesByAttachTypeV3(),
      domainAdminNavigationEntriesV3: this.getDomainAdminNavigationEntriesByAttachTypeV3(),
      totemInfoByRoute: this.getTotemInfoByRoute(),
    }
  }

  /**
   * Start the app with lazy domain loading.
   * This allows the account details fetch to run in parallel with domain import,
   * potentially saving hundreds of milliseconds on initial load.
   *
   * Key optimization: ApolloProvider and AppProviderPreloader are placed ABOVE
   * the CoreContext.Provider, so the account fetch starts immediately while
   * domains are still loading.
   *
   * @param domainImportPromise - Promise that resolves to the domain registry module
   * @param callback - Render callback (receives React tree)
   */
  start<T extends (tree: ReactElement) => any>(
    domainImportPromise: Promise<{ registerDomains: (app: RPPCore) => void }>,
    callback: T
  ) {
    // Prefetch AppUIProviders chunk - starts download immediately while still lazy loaded.
    // By the time React.lazy needs it, the chunk will already be cached.
    importAppUIProviders()

    const history = createHistory()

    // Determine initial locale early so we can load lazy translations
    const initialLocale = getStoredLocaleWithFallback()

    // Promise that resolves when domains are registered AND lazy translations are loaded
    const domainRegistrationPromise = domainImportPromise.then(async ({ registerDomains }) => {
      registerDomains(this)
      // Load lazy translations for non-English locales after domains are registered
      await this.loadLazyTranslations(initialLocale)
    })

    // Component that waits for domains before providing full context and rendering children
    // This is the "domain gate" - children only render after domains are registered
    const LazyContextProvider = ({ children }: { children: ReactElement }) => {
      const [contextValue, setContextValue] = useState<RPPCoreContext | null>(null)

      useEffect(() => {
        domainRegistrationPromise.then(() => {
          const ctx = this.prepareContext()
          setContextValue(ctx)
        })
      }, [])

      // Don't render children until context is ready (domains loaded)
      if (!contextValue) {
        return null
      }

      return <CoreContext.Provider value={contextValue}>{children}</CoreContext.Provider>
    }

    // This wrapper provides locale-aware messages to AppProvider
    const LocaleAwareApp = () => {
      const { locale } = useI18nContext()
      loadDayjsLocale(locale)
      loadMomentLocale(locale)
      const messages = this.getTranslations(locale)
      return <AppProvider messages={messages} locale={locale} />
    }

    // IMPORTANT: Structure is designed for optimal parallelization:
    // - MockGate blocks everything in test environments until mocks are ready
    // - ApolloProvider is above LazyContextProvider so Apollo queries can start immediately
    // - AppProviderPreloader starts account fetch while domains are still loading
    // - LazyContextProvider waits for domains before providing CoreContext
    // - AppProvider renders after both account AND domains are ready
    return callback(
      <MockGate>
        <I18nProvider>
          <ApolloProvider client={fetchDashboardClient()}>
            <QueryClientProvider client={queryClient}>
              <Router history={history}>
                <Favicon url={RPP_FAVICON} />
                <AppProviderPreloader>
                  <LazyContextProvider>
                    <LocaleAwareApp />
                  </LazyContextProvider>
                </AppProviderPreloader>
              </Router>
            </QueryClientProvider>
          </ApolloProvider>
        </I18nProvider>
      </MockGate>
    ) as ReturnType<T>
  }
}
